import {createContext, FC, ReactNode, useContext, useEffect, useState} from "react";
import {LoadEventsEnum} from "../types/enums";
import {OrganizationAdminDto, ParticipantAdminDto} from "../types/contracts";
import {useDebounce} from "use-debounce";
import {useEventsContext} from "./EventsProvider";
import apiService from "../services/api-service";

type Props = {
    type: LoadEventsEnum;
    children: ReactNode;
}

type OrganizationsAndParticipantsContextModel = {
  participants: ParticipantAdminDto[],
  organizations: OrganizationAdminDto[],
  pageNumber: number,
  pageSize: number,
  totalPages: number,
  selectedEventId: string | null,
  setSelectedEventId: (eventId: string) => void,
  setPageNumber: (pageNumber: number) => void,
  setPageSize: (pageSize: number) => void,
  nameFilter: string,
  setNameFilter: (query: string) => void,
  isLoading: boolean,
}

const OrganizationsAndParticipantsContext = createContext<OrganizationsAndParticipantsContextModel | undefined>(undefined)

export const OrganizationsAndParticipantsProvider: FC<Props> = ({children, type}) => {

  const [participants, setParticipants] = useState<ParticipantAdminDto[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationAdminDto[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [nameFilter, setNameFilter] = useState<string>("");
  const [debounceNameFilter] = useDebounce(nameFilter, 300);
  const [isLoading, setIsLoading] = useState(true);

  const {
    selectedEventId,
    setSelectedEventId,
    events,
  } = useEventsContext();

  const loadOrganizationsOrParticipants = async () => {
    if (!selectedEventId || !events || events.length === 0) return;

    try {
      setIsLoading(true);

      if (type === LoadEventsEnum.organizations) {
        const {data} = await apiService.fetchOrganizations(
          selectedEventId,
          pageNumber,
          pageSize,
          nameFilter
        );
        setOrganizations(data.items);
        setTotalPages(data.totalPages);
      } else {
        const {data} = await apiService.fetchParticipants(
          selectedEventId,
          pageNumber,
          pageSize,
          debounceNameFilter
        );
        setParticipants(data.items);
        setTotalPages(data.totalPages);
      }
    } catch (err) {
      console.error("Failed to fetch data", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizationsOrParticipants();
  }, [pageNumber, pageSize, selectedEventId, debounceNameFilter, type]);


  return <OrganizationsAndParticipantsContext.Provider
    value={{
      participants,
      organizations,
      pageNumber,
      pageSize,
      totalPages,
      selectedEventId,
      setSelectedEventId,
      setPageNumber,
      setPageSize,
      nameFilter,
      setNameFilter,
      isLoading,
    }}
  >
    {children}
  </OrganizationsAndParticipantsContext.Provider>
}

export const useOrganizationsAndParticipantsContext = () => {
  const userContext = useContext(OrganizationsAndParticipantsContext);
  if (!userContext) {
    throw new Error('useLoadEventsContext must be used with LoadEventsProvider');
  }
  return userContext;
}